import { FC, useContext, useState } from "react";
import { onlineManager, useQuery } from "react-query";
import { isUndefined, noop } from "lodash";
import { useFormikContext } from "formik";
import {
  CalculateMixesRequest,
  CalculateMixesResponse,
} from "../@types/responses";
import { MAP, MapLegendItem, MixesValues, TrackCode } from "../@types/common";
import { BREAKPOINTS } from "../constants/common";
import { translation as t } from "../utils/translation";
import { ICONS } from "../assets";
import { ReactComponent as ChevronRight } from "../assets/icons/chevron-right.svg";
import { ReactComponent as ChevronLeft } from "../assets/icons/chevron-left.svg";
import { APIContext } from "../context/context";
import { useWindowSize } from "../hooks/useWindowWidth";
import { getPayloadMixes } from "../utils/logic";
import MixResults from "./mixResults";
import ResultsHeader from "./resultsHeader";
import ResultsLinks, { MortgageRequestButton } from "./resultsLinks";
import ErrorModal from "./errorModal";
import Loader from "./loader";
import Swiper from "swiper";
import { Swiper as SwiperComponent, SwiperSlide } from "swiper/react";
// Import Swiper styles
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/scrollbar";
interface ResultsHeaderProps {
  loanAmount: number;
  period: number;
}
const MixesResults: FC<ResultsHeaderProps> = ({
  loanAmount,
  period,
}: ResultsHeaderProps) => {
  const API_AND_URLS = useContext(APIContext);
  const url =
    API_AND_URLS !== undefined ? API_AND_URLS.API.POST.CALCULATE_MIXES : "";
  const [activeIndex, setActiveIndex] = useState(1);
  const [swiper, setSwiper] = useState<Swiper>();
  const [open, setOpen] = useState(true);
  const { values } = useFormikContext<MixesValues>();
  const windowWidth = useWindowSize();
  const { MEDIUM } = BREAKPOINTS;
  const params: CalculateMixesRequest = {
    amount: loanAmount,
    period: period,
    returnPaymentDetails: false,
    calculatePrimeDaily: false,
  };
  const { isLoading, error, data } = useQuery<
    CalculateMixesResponse,
    Error,
    CalculateMixesResponse,
    [string, CalculateMixesRequest]
  >(
    ["CalculateMixes", params],
    () =>
      // for Development
      // fetch(url).then((response) => {
        // for Production
        fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(params),
        }).then((response) => {
          if (response.status !== 200) {
            throw new Error();
          }
        // END for Production
        return response.json();
      }),
    {
      retry: false,
      enabled: onlineManager.isOnline(),
    }
  );
  if (isLoading) {
    return <Loader />;
  }
  if (error || isUndefined(data))
    return <ErrorModal open={open} setOpen={setOpen} />;
  return (
    <>
      <ResultsHeader loanAmount={loanAmount} period={period} />
      <MortgageRequestButton params={getPayloadMixes(values)} position={'Top'} />
      <div
        className={`flex flex-col items-center 
               ${windowWidth > MEDIUM ? "" : "-mx-5"}`}
      >
        <div
          className={`flex flex-row justify-center items-center relative overflow-hidden
         ${windowWidth > MEDIUM ? "w-auto" : "w-[100%] "}`}
        >
          {windowWidth <= MEDIUM ? (
            <SwiperComponent
              spaceBetween={20}
              centeredSlides={true}
              initialSlide={1}
              loop={false}
              slidesPerView={1.3}
              onSlideChange={(e) => {
                setActiveIndex(e.activeIndex);
              }}
              onSwiper={(s) => setSwiper(s)}
            >
              <ChevronRight
                className={`absolute right-0 top-[313px] z-10 ${
                  activeIndex > 0 ? "stroke-blue" : "stroke-lightBlue01"
                }`}
                onClick={
                  activeIndex > 0
                    ? () => {
                        swiper?.slideTo(activeIndex - 1);
                      }
                    : noop
                }
                title={t.ACCESSIBILITY.BEYOND_THE_BASKET_RESULT_RIGHT}
              />
              <ChevronLeft
                className={`absolute left-0 top-[313px] z-10 ${
                  activeIndex < 2 ? "stroke-blue" : "stroke-lightBlue01"
                }`}
                onClick={
                  activeIndex < 2
                    ? () => {
                        swiper?.slideTo(activeIndex + 1);
                      }
                    : noop
                }
                title={t.ACCESSIBILITY.BEYOND_THE_BASKET_RESULT_LEFT}
              />
              {data.mixes.map((mix, i) => {
                const name = mix.mixName;
                const firstPayment = mix.repaymentSummary.totalFirstPayment;
                const highestPayment = mix.repaymentSummary.totalHighestPayment;
                const totalAmount = mix.repaymentSummary.totalAmountPaid;
                const totalIrr = mix.repaymentSummary.totalIrr;
                const productParts: MapLegendItem[] = [];
                mix.repaymentSummary.loansRepayment.map((loan) => {
                  const productPart: MapLegendItem = {
                    color: MAP[loan.product.productCode as TrackCode],
                    percent: loan.product.productPart,
                    text: loan.product.trackName,
                  };
                  productParts.push(productPart);
                });
                return (
                  <SwiperSlide>
                    <MixResults
                      name={name}
                      firstPayment={firstPayment}
                      highestPayment={highestPayment}
                      totalAmount={totalAmount}
                      totalIrr={totalIrr}
                      ProductParts={productParts}
                    />
                  </SwiperSlide>
                );
              })}
              <div className={`flex items-center justify-center	p-[29px]`}>
                {data.mixes.map((_, index) => (
                  <img
                    key={index}
                    className={`${index == activeIndex ? "mx-[3px]" : "mx-0"}`}
                    onClick={() => {
                      swiper?.slideTo(index);
                    }}
                    src={
                      ICONS[
                        `${
                          index == activeIndex ? "ELLIPSE_SELECTED" : "ELLIPSE"
                        }`
                      ]
                    }
                    alt={t.ACCESSIBILITY.SCROLL_BUTTON_FOR_BASKET_RESULT}
                    title={t.ACCESSIBILITY.SCROLL_BUTTON_FOR_BASKET_RESULT}
                  />
                ))}
              </div>
            </SwiperComponent>
          ) : (
            <>
              {data.mixes.map((mix, i) => {
                const name = mix.mixName;
                const firstPayment = mix.repaymentSummary.totalFirstPayment;
                const highestPayment = mix.repaymentSummary.totalHighestPayment;
                const totalAmount = mix.repaymentSummary.totalAmountPaid;
                const totalIrr = mix.repaymentSummary.totalIrr;
                const productParts: MapLegendItem[] = [];
                mix.repaymentSummary.loansRepayment.map((loan) => {
                  const productPart: MapLegendItem = {
                    color: MAP[loan.product.productCode as TrackCode],
                    percent: loan.product.productPart,
                    text: loan.product.trackName,
                  };
                  productParts.push(productPart);
                });
                return (
                  <MixResults
                    name={name}
                    firstPayment={firstPayment}
                    highestPayment={highestPayment}
                    totalAmount={totalAmount}
                    totalIrr={totalIrr}
                    ProductParts={productParts}
                  />
                );
              })}
            </>
          )}
        </div>
        <ResultsLinks params={getPayloadMixes(values)} position={'bottom'}/>
      </div>
    </>
  );
};
export default MixesResults;